import { useState } from 'react';
import { Button, Empty, Space, Spin, Typography } from 'antd';
import { useListStagingAreasQuery, useListTrackedCardsQuery } from '../../../redux/api/kit-staging';
import { StatusLegendPopup } from '../../../components/KitStaging/StatusLegendPopup';
import { ShelfBoardView } from '../../../components/KitStaging/ShelfBoardView';
import { AreaSubTabs } from '../../../components/KitStaging/AreaSubTabs';
import { groupByOli, OliGroup } from './groupByOli';

interface Props {
	/** Board area key — 'jsa' or 'msa'. */
	area: 'jsa' | 'msa';
	title: string;
	/** Staging-area type to filter the rack grids on. */
	areaType: 'job_staging' | 'material_staging';
	/** localStorage key for the AreaSubTabs star-pin default. */
	storageKey: string;
	selectedGroup?: OliGroup | null;
	onSelect?: (g: OliGroup | null) => void;
}

/** Shared rack/shelf board for JSA + MSA. Sub-tabs across the active staging
 *  areas of the given type; each renders a ShelfBoardView grid. Tapping a card
 *  tile selects its OLI group into the shared left sidebar. Cards mid-DUAL into
 *  this area type render on their destination shelf with inline Confirm/Undo
 *  (via allGroups). */
export function ShelfBoardScaffold({ area, title, areaType, storageKey, selectedGroup = null, onSelect }: Props) {
	const { data: cards, isLoading } = useListTrackedCardsQuery({}, { pollingInterval: 10000 });
	const { data: stagingAreas } = useListStagingAreasQuery(undefined, { pollingInterval: 10000 });
	const [activeAreaId, setActiveAreaId] = useState<number | null>(null);
	const [legendOpen, setLegendOpen] = useState(false);

	const allGroups = groupByOli(cards ?? []);
	const groups = allGroups.filter(
		(g) => g.kitCard?.current_area === area || g.materialCard?.current_area === area,
	);
	const subAreas = (stagingAreas ?? []).filter((a) => a.area_type === areaType && a.is_active);
	const selectedArea = subAreas.find((a) => a.id === activeAreaId) ?? null;

	// ShelfBoardView toggles on re-tap internally — just forward.
	const select = (g: OliGroup | null) => onSelect?.(g);

	return (
		<div style={{ padding: 16, width: '100%' }}>
			<Space style={{ justifyContent: 'space-between', width: '100%' }}>
				<Typography.Title level={3} style={{ margin: 0 }}>
					{title}{' '}
					<Typography.Text type="secondary" style={{ fontSize: 14 }}>
						({groups.length})
					</Typography.Text>
				</Typography.Title>
				<Button onClick={() => setLegendOpen(true)} aria-label="Show status legend">
					?
				</Button>
			</Space>

			<div style={{ marginTop: 12 }}>
				{isLoading ? (
					<div style={{ padding: 48, textAlign: 'center' }}>
						<Spin />
					</div>
				) : subAreas.length === 0 ? (
					<Empty style={{ marginTop: 48 }} description={`No active ${title} areas`} />
				) : (
					<>
						<AreaSubTabs areas={subAreas} value={activeAreaId} onChange={setActiveAreaId} storageKey={storageKey} />
						{selectedArea ? (
							<ShelfBoardView
								area={selectedArea}
								groups={groups}
								allGroups={allGroups}
								currentArea={area}
								onSelect={select}
								selectedGroup={selectedGroup}
							/>
						) : null}
					</>
				)}
			</div>

			<StatusLegendPopup open={legendOpen} onClose={() => setLegendOpen(false)} />
		</div>
	);
}
